import { ChartOrientation, CustomShape } from '@statsbomb/kitbag-datavis';

import { getFilterScope } from '@statsbomb/kitbag-global-football/state';
import { EventTypeFilterGroup, FilterSchema, Gender } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { filterScopeIdAtom, filtersAtom } from '@/atoms/filters/filter';
import { widgetGenderAtom } from '@/atoms/queries/userConfigs';
import { radarTemplateIdAtom } from '@/atoms/radar';
import { orderByAtom, orderDirectionAtom } from '@/atoms/table';
import {
  barChartOrientationAtom,
  isStartPlacementAtom,
  pitchCropAtom,
  pitchMarkerColouringModeAtom,
  pitchOrientationReportWidgetAtom,
  showArrowsAtom,
} from '@/atoms/visualisation';
import { PITCH_CROP, PITCH_ORIENTATION } from '@/consts/visualisations';
import { NullableMetricDistributionRange } from '@/types/metric';
import { SORT_DIRECTION } from '@/types/ordering';
import {
  ConfigDisplaySettingAtomHydrateCallbacks,
  ConfigDisplaySettingAtoms,
  ConfigEventFilterAtomHydrateCallbacks,
  ConfigEventFilterAtoms,
  ConfigFilterAtomHydrateCallbacks,
  ConfigFilterAtomValues,
  ConfigFilterAtoms,
  ConfigGameFilterAtomHydrateCallbacks,
  ConfigGameFilterAtoms,
  ConfigPlayerFilterAtomHydrateCallbacks,
  ConfigPlayerFilterAtoms,
} from '@/types/userConfigs';
import { PitchCrop, PitchOrientation } from '@/types/visualisation';
import { convertFiltersToOption } from '@/utils/array';
import { gameTypeOptions } from './gameMetrics';
import { GAME_RANGE_FILTER_OPTIONS, GAME_RANGE_OPTIONS, PERIOD_RANGE_OPTIONS } from './gameRange';

const mirrorFilterAtom = <K extends keyof ConfigFilterAtomValues>(filterKey: K) =>
  atom(
    get => get(filtersAtom)[filterKey],
    // Merge over the live value of the CURRENT scope (resolved via
    // filterScopeIdAtom, so a widget hydrates its own scope) rather than this
    // store's snapshot, which can be stale in a store that never mounted a
    // filter consumer (e.g. pure-atom tests). Writing through filtersAtom keeps
    // the local snapshot in sync too.
    (get, set, update: ConfigFilterAtomValues[K]) =>
      set(filtersAtom, { ...getFilterScope(get(filterScopeIdAtom)).get(), [filterKey]: update }),
  );

const gameFilterAtoms: ConfigGameFilterAtoms = {
  periodRange: mirrorFilterAtom('periodRange'),
  gameRange: mirrorFilterAtom('gameRange'),
  startDate: mirrorFilterAtom('startDate'),
  endDate: mirrorFilterAtom('endDate'),
  gameType: mirrorFilterAtom('gameType'),
  deselectedGameIds: mirrorFilterAtom('deselectedGameIds'),
};

const eventFilterAtoms: ConfigEventFilterAtoms = {
  eventNames: mirrorFilterAtom('eventNames'),
  outcomes: mirrorFilterAtom('outcomes'),
  bodyParts: mirrorFilterAtom('bodyParts'),
  playPatterns: mirrorFilterAtom('playPatterns'),
  pitchStartZones: mirrorFilterAtom('pitchStartZones'),
  pitchEndZones: mirrorFilterAtom('pitchEndZones'),
  minObv: mirrorFilterAtom('minObv'),
  maxObv: mirrorFilterAtom('maxObv'),
  minXg: mirrorFilterAtom('minXg'),
  maxXg: mirrorFilterAtom('maxXg'),
  eventFilterGroups: mirrorFilterAtom('eventFilterGroups'),
};

const playerFilterAtoms: ConfigPlayerFilterAtoms = {
  minAge: mirrorFilterAtom('minAge'),
  maxAge: mirrorFilterAtom('maxAge'),
  minHeight: mirrorFilterAtom('minHeight'),
  maxHeight: mirrorFilterAtom('maxHeight'),
  minMinutes: mirrorFilterAtom('minMinutes'),
  maxMinutes: mirrorFilterAtom('maxMinutes'),
  nationality: mirrorFilterAtom('nationality'),
  preferredFoot: mirrorFilterAtom('preferredFoot'),
};

const matchFilterAtoms = {
  matchPeriods: mirrorFilterAtom('matchPeriods'),
  eventsPerspective: mirrorFilterAtom('eventsPerspective'),
  minMatchMinute: mirrorFilterAtom('minMatchMinute'),
  maxMatchMinute: mirrorFilterAtom('maxMatchMinute'),
};

export const metricKeyHydrateAtom = mirrorFilterAtom('metric');

export const filterAtomsToHydrate = (): ConfigFilterAtoms => ({
  competitionIds: mirrorFilterAtom('competitionIds'),
  seasonIds: mirrorFilterAtom('seasonIds'),
  cycleIds: mirrorFilterAtom('cycleIds'),
  teamIds: mirrorFilterAtom('teamIds'),
  playerIds: mirrorFilterAtom('playerIds'),

  positions: mirrorFilterAtom('positions'),

  metric: metricKeyHydrateAtom,
  metricDistributions: mirrorFilterAtom('metricDistributions'),
  gender: widgetGenderAtom,

  ...gameFilterAtoms,
  ...eventFilterAtoms,
  ...playerFilterAtoms,
  ...matchFilterAtoms,
});

const gameFilterAtomHydrateCallbacks: ConfigGameFilterAtomHydrateCallbacks = {
  periodRange: ({ value }: { value: number }) => PERIOD_RANGE_OPTIONS.find(option => option.value === value)!,
  gameRange: (value: string) =>
    GAME_RANGE_FILTER_OPTIONS.find(option => option.key === value) || GAME_RANGE_OPTIONS.period,
  startDate: (value: string) => value,
  endDate: (value: string) => value,
  gameType: (value: string) => gameTypeOptions.find(option => option.value === value)!,
  deselectedGameIds: (value: number[]) => convertFiltersToOption(value),
};

const eventFilterGroupSchema = FilterSchema.shape.eventFilterGroups.unwrap().element;

const eventFilterAtomHydrateCallbacks: ConfigEventFilterAtomHydrateCallbacks = {
  eventNames: (value: string[]) => value,
  outcomes: (value: string[]) => value,
  bodyParts: (value: string[]) => value,
  playPatterns: (value: string[]) => value,
  pitchStartZones: (value: CustomShape[]) => value,
  pitchEndZones: (value: CustomShape[]) => value,
  minObv: (value: number) => value,
  maxObv: (value: number) => value,
  minXg: (value: number) => value,
  maxXg: (value: number) => value,
  eventFilterGroups: (value: EventTypeFilterGroup[]) =>
    (value ?? []).flatMap(group => {
      const result = eventFilterGroupSchema.safeParse(group);
      return result.success ? [result.data] : [];
    }),
};

const playerFilterAtomHydrateCallbacks: ConfigPlayerFilterAtomHydrateCallbacks = {
  minAge: (value: number) => value,
  maxAge: (value: number) => value,
  minHeight: (value: number) => value,
  maxHeight: (value: number) => value,
  minMinutes: (value: number) => value,
  maxMinutes: (value: number) => value,
  nationality: (value: string[]) => value,
  preferredFoot: (value: string[]) => value,
};

const matchFilterAtomHydrateCallbacks = {
  matchPeriods: (value: number[]) => value,
  eventsPerspective: (value: unknown) => (value === 'against' ? 'against' : 'for'),
  minMatchMinute: (value: number) => value,
  maxMatchMinute: (value: number) => value,
};

export const filterAtomsToHydrateCallback: ConfigFilterAtomHydrateCallbacks = {
  competitionIds: (value: number[]) => value,
  seasonIds: (value: number[] | null) => value,
  cycleIds: (value: number[]) => value,
  teamIds: (value: number[]) => value,
  playerIds: (value: number[]) => value,

  positions: (value: string[]) => value,

  metric: (value: string) => value,
  // We now save undefined to the DB for unset min/max, but there are some old configs with null so need to convert
  // these to undefined
  metricDistributions: (value: NullableMetricDistributionRange[]) =>
    value.map(({ name, min, max }) => ({ name, min: min || undefined, max: max || undefined })),
  gender: (value: Gender) => value,

  ...gameFilterAtomHydrateCallbacks,
  ...eventFilterAtomHydrateCallbacks,
  ...playerFilterAtomHydrateCallbacks,
  ...matchFilterAtomHydrateCallbacks,
};

export const displayAtomsToHydrate = (): ConfigDisplaySettingAtoms => ({
  radarTemplateId: radarTemplateIdAtom,
  pitchOrientation: pitchOrientationReportWidgetAtom,
  pitchCrop: pitchCropAtom,
  showArrows: showArrowsAtom,
  isStartPlacement: isStartPlacementAtom,
  pitchMarkerColouringMode: pitchMarkerColouringModeAtom,
  orderBy: orderByAtom,
  orderDirection: orderDirectionAtom,
  barChartOrientation: barChartOrientationAtom,
});

export const displayAtomsToHydrateCallback: ConfigDisplaySettingAtomHydrateCallbacks = {
  radarTemplateId: (value: number) => value,
  pitchOrientation: (value: PitchOrientation) => PITCH_ORIENTATION.find(orientation => orientation === value)!,
  pitchCrop: (value: PitchCrop) => PITCH_CROP.find(crop => crop === value)!,
  showArrows: (value: boolean) => value,
  isStartPlacement: (value: boolean) => value,
  pitchMarkerColouringMode: (value: string) => value,
  orderBy: (value: string) => value,
  orderDirection: (value: SORT_DIRECTION) => value,
  barChartOrientation: (value: ChartOrientation) => value,
};
